import React, { useState, useEffect } from 'react';
import { tokenManager } from '../routers/api';
import { Play, Clock, CheckCircle, User, LogOut, BookOpen, FileText, Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from "../routers/axios";
import { authAPI } from "../routers/api";

const StudentTestDashboard = () => {
  const [studentName, setStudentName] = useState("");
  const [tests, setTests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // 🆕 Thêm các state cho modal chi tiết
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [writingDetail, setWritingDetail] = useState(null);

  useEffect(() => {
    const user = tokenManager.getUser();
    if (user) {
      setStudentName(user.firstName + " " + user.lastName);
    }
  }, []);

  useEffect(() => {
    const fetchTests = async () => {
      try {
        const res = await axios.get("/test/list");
        const testsData = res.data || [];

        const user = tokenManager.getUser();
        if (user) {
          const enrichedTests = await Promise.all(
            testsData.map(async (t) => {
              try {
                const res = await axios.get(`/test_attempts/${user.id}/${t.id}`);
                const testStatus = res.data?.status || "not_started";
                const attempts = res.data?.attempts || [];
                const totalScenarios = t.scenarios?.length || attempts.length || 0;
                const completedScenarios = attempts.filter(a => a.status === "completed").length;
                return {
                  ...t,
                  status: testStatus,
                  progress: `${completedScenarios}/${totalScenarios}`
                };
              } catch (err) {
                console.warn(`⚠️ Không lấy được trạng thái của test ${t.id}`, err);
                return { ...t, status: "not_started" };
              }
            })
          );
          setTests(enrichedTests);
        } else {
          setTests(testsData.map(t => ({ ...t, status: "not_started" })));
        }
      } catch (err) {
        console.error("Error fetching tests:", err);
      }
    };

    const fetchNotifications = async () => {
      try {
        const res = await axios.get(`/notifications/list`);
        // ✅ backend trả về { data: [...], unread_count: N }
        const list = res.data?.data || [];
        setNotifications(list);
      } catch (err) {
        console.error("❌ Lỗi khi lấy thông báo:", err);
        setNotifications([]); // đảm bảo không undefined
      }
    };


    fetchTests();
    fetchNotifications();

    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  const navigate = useNavigate();

  const getStatusInfo = (status) => {
    switch (status) {
      case "completed":
        return {
          text: "✅ Completed",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
          buttonText: "View Results",
          buttonColor: "bg-gray-500 hover:bg-gray-600"
        };
      case "in_progress":
        return {
          text: "⏳ In Progress",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
          buttonText: "Continue",
          buttonColor: "bg-blue-600 hover:bg-blue-700"
        };
      default:
        return {
          text: "🕒 Not Started",
          bgColor: "bg-white",
          borderColor: "border-gray-200",
          buttonText: "Start Test",
          buttonColor: "bg-emerald-600 hover:bg-emerald-700"
        };
    }
  };

  const handleStartTest = (test) => {
    if (test.status === "completed") {
      navigate(`/student/test/${test.id}`, {
        state: { testData: test, testId: test.id, viewMode: true },
      });
      return;
    }

    navigate(`/student/test/${test.id}`, {
      state: { testData: test, testId: test.id, viewMode: false },
    });

    if (test.status === "not_started") {
      setTests((prevTests) =>
        prevTests.map((t) =>
          t.id === test.id ? { ...t, status: "in_progress" } : t
        )
      );
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "Beginner": return "bg-green-100 text-green-700";
      case "Intermediate": return "bg-yellow-100 text-yellow-700";
      case "Advanced": return "bg-red-100 text-red-700";
      case "Training": return "bg-blue-100 text-blue-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const handleLogout = async () => {
    await authAPI.logout();
    navigate("/student/login");
  };

  // 🆕 Hàm xử lý khi click vào 1 thông báo
  const handleNotificationClick = async (notification) => {
    try {
      setSelectedNotification(notification);

      // 🟢 Nếu chưa đọc, gửi request mark-read
      if (!notification.is_read) {
        try {
          await axios.post(`/notifications/mark-read/${notification.id}`);
          setNotifications(prev =>
            prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
          );
        } catch (err) {
          console.warn("⚠️ Không thể đánh dấu đã đọc:", err);
        }
      }

      // 🟢 Gọi API lấy chi tiết bài viết
      const res = await axios.get(`/test_attempts/by-attempt/${notification.attempt_id}`);
      const detail = res.data.data || res.data;

      if (!detail) {
        alert("Không tìm thấy dữ liệu chi tiết bài viết.");
        return;
      }

      console.log("🧩 Writing detail:", detail);
      setWritingDetail(detail);
      setShowDetailModal(true);
    } catch (err) {
      console.error("❌ Lỗi khi lấy chi tiết bài viết:", err);
      alert("Không thể tải chi tiết bài viết, vui lòng thử lại sau.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">V-SBA EAP Test</span>
            </div>

            <div className="flex items-center space-x-4">
              {/* 🔔 Notification Bell */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
                >
                  <Bell className="w-6 h-6 text-gray-700" />
                  {notifications.some(n => !n.is_read) && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5">
                      {notifications.filter(n => !n.is_read).length}
                    </span>
                  )}

                </button>

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white shadow-lg rounded-xl border border-gray-200 z-50">
                    <div className="flex justify-between items-center px-4 py-2 border-b">
                      <span className="font-semibold text-gray-700">Notifications</span>
                      <button
                        onClick={() => setShowNotifications(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {notifications.length === 0 ? (
                      <p className="text-gray-500 text-sm px-4 py-3 text-center">Không có thông báo nào</p>
                    ) : (
                      <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                        {notifications.map((n) => (
                          <li
                            key={n.id}
                            onClick={() => handleNotificationClick(n)}
                            className={`px-4 py-3 cursor-pointer transition-all duration-200
                            ${n.is_read ? "bg-white hover:bg-blue-50" : "bg-gray-100 hover:bg-gray-200"}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-sm text-gray-800 font-medium">
                                  {n.message.split(" – ")[0] || "Thông báo"}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {n.message.split(" – ")[1] || ""}
                                </p>
                              </div>
                              {!n.is_read && (
                                <span className="ml-2 text-xs bg-blue-500 text-white font-semibold px-2 py-0.5 rounded-full">
                                  Mới
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(n.created_at).toLocaleString("vi-VN")}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}

                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-emerald-400 to-blue-500 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <span className="text-gray-700 font-medium">
                  Hello, {studentName} 👋
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium">Log Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FileText className="w-8 h-8 text-blue-600" />
            <span>Available Tests</span>
          </h1>
          <p className="text-gray-600 mt-2">Select a test to start or continue your assessment</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tests.map((test) => {
            const totalMinutes = test.scenarios?.reduce((sum, s) => sum + (s.duration_minutes || 0), 0) || 0;
            const scenarioCount = test.scenarios?.length || 0;
            const statusInfo = getStatusInfo(test.status || "not_started");

            return (
              <div key={test.id} className={`p-5 rounded-xl shadow-md hover:shadow-lg transition border ${statusInfo.borderColor} ${statusInfo.bgColor}`}>
                <h3 className="text-xl font-semibold">{test.test_name}</h3>
                <p className="text-gray-600">{test.description}</p>
                {test.difficulty && (
                  <span className={`inline-block mt-2 px-3 py-1 text-sm rounded-lg font-medium ${getDifficultyColor(test.difficulty)}`}>
                    {test.difficulty}
                  </span>
                )}
                <div className="flex items-center gap-4 mt-3 text-gray-700">
                  <span className="flex items-center gap-1">⏱ {totalMinutes} minutes</span>
                  <span className="flex items-center gap-1">📑 {scenarioCount} scenarios</span>
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {test.scenarios?.map((s) => (
                    <span key={s.id} className="px-3 py-1 text-sm rounded-lg bg-blue-100 text-blue-600" dangerouslySetInnerHTML={{ __html: s.scenario_name }} />
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {test.status === "completed" && (
                    <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Completed
                    </span>
                  )}
                  {test.status === "in_progress" && (
                    <span className="px-3 py-1 text-sm rounded-full bg-yellow-100 text-yellow-700 flex items-center gap-1">
                      <Clock className="w-4 h-4" /> In Progress
                    </span>
                  )}
                  {!test.status || test.status === "not_started" ? (
                    <span className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-600 flex items-center gap-1">
                      <Play className="w-4 h-4" /> Not Started
                    </span>
                  ) : null}
                </div>
                <button onClick={() => handleStartTest(test)} className={`mt-4 w-full text-white py-2 rounded-lg transition ${statusInfo.buttonColor}`}>
                  {statusInfo.buttonText}
                </button>
              </div>
            );
          })}
        </div>
      </main>

      {/* 🆕 Modal hiển thị chi tiết bài viết */}
      {showDetailModal && writingDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl p-6 relative">
            <button
              onClick={() => setShowDetailModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-2 mb-4 border-b pb-2">
              <FileText className="w-6 h-6 text-blue-500" />
              <h2 className="text-2xl font-semibold text-gray-900">Bài viết của bạn</h2>
            </div>

            {/* Meta Info */}
            <div className="text-sm text-gray-700 space-y-1 mb-4">
              <p><strong>📘 Bài test:</strong> {writingDetail.test_name || "Không xác định"}</p>
              <p><strong>🧩 Scenario:</strong> {writingDetail.scenario_name || `Scenario #${writingDetail.scenario_id}`}</p>
              <p><strong>👩‍🏫 Giáo viên chấm:</strong> {writingDetail.teacher_name || "Chưa rõ"}</p>
              <p><strong>⏰ Ngày chấm:</strong> {new Date(writingDetail.graded_at).toLocaleString("vi-VN")}</p>
            </div>

            {/* Writing Content */}
            <div className="bg-gray-50 border rounded-lg p-4 mb-4 max-h-80 overflow-y-auto prose prose-sm">
              <div dangerouslySetInnerHTML={{ __html: writingDetail.writing_content }} />
            </div>

            {/* Score + Feedback */}
            <div className="space-y-3">
              <p className="text-lg font-semibold">
                <strong>Điểm:</strong>{" "}
                <span className="text-green-600">{writingDetail.writing_score}/10</span>
              </p>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                <p className="font-semibold flex items-center gap-2 text-gray-800">
                  💬 Nhận xét của giáo viên:
                </p>
                <p className="italic text-gray-700 mt-1">
                  {writingDetail.feedback || "Chưa có nhận xét."}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}


      <footer className="mt-16 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-500">
            © 2025 English Test System. Designed for better learning experience.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StudentTestDashboard;
